import {
  Link,
  createFileRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router'
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Upload,
  message,
} from 'antd'
import {
  DownloadOutlined,
  SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { useState } from 'react'
import {
  useCreatePosition,
  useDeletePosition,
  useDeletePositionsByBatchId,
  useDownloadTemplate,
  useImportPositions,
  usePositions,
  useUpdatePosition,
} from '../hooks/usePositions'
import { useBatches } from '../hooks/useBatches'
import { getPagination } from '../components/TablePagination'
import { formatPositionCodeName } from '../utils/positionFormatter'
import type { ColumnsType } from 'antd/es/table'
import type {
  PositionData,
  PositionQueryParams,
} from '../services/positionService'

// 定义搜索参数类型
interface PositionSearchParams {
  pageNum?: number
  pageSize?: number
  name?: string
  batchId?: number
}

export const Route = createFileRoute('/positionCode')({
  component: PositionCodePage,
  validateSearch: (search: Record<string, unknown>): PositionSearchParams => {
    return {
      pageNum: Number(search.pageNum) || 1,
      pageSize: Number(search.pageSize) || 10,
      name: (search.name as string) || undefined,
      batchId: Number(search.batchId) || undefined,
    }
  },
})

function PositionCodePage() {
  // 获取 URL 搜索参数和导航函数
  const search = useSearch({ from: '/positionCode' })
  const navigate = useNavigate({ from: '/positionCode' })

  // 状态管理
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)
  const [isBatchDeleteModalOpen, setIsBatchDeleteModalOpen] = useState(false)
  const [currentPosition, setCurrentPosition] = useState<PositionData | null>(
    null,
  )
  const [fileList, setFileList] = useState<Array<any>>([])
  const [form] = Form.useForm()
  const [importForm] = Form.useForm()
  const [batchDeleteForm] = Form.useForm()

  // 从 URL 参数构建查询参数
  const queryParams: PositionQueryParams = {
    pageNum: search.pageNum || 1,
    pageSize: search.pageSize || 10,
    name: search.name,
    batchId: search.batchId,
  }

  // 更新 URL 搜索参数的函数
  const updateSearchParams = (updates: Partial<PositionSearchParams>) => {
    navigate({
      search: (prev) => ({ ...prev, ...updates }),
    })
  }

  // React Query hooks
  const { data: positionsData, isLoading, error } = usePositions(queryParams)
  const { data: batches } = useBatches()
  const createMutation = useCreatePosition()
  const updateMutation = useUpdatePosition()
  const deleteMutation = useDeletePosition()
  const downloadMutation = useDownloadTemplate()
  const importMutation = useImportPositions()
  const batchDeleteMutation = useDeletePositionsByBatchId()

  // 表格列定义
  const columns: ColumnsType<PositionData> = [
    {
      title: '岗位 ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: '岗位名称',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => formatPositionCodeName(name),
      filteredValue: search.name ? [search.name] : undefined,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="搜索岗位名称"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => {
              confirm?.()
              updateSearchParams({
                name: selectedKeys[0] as string,
                pageNum: 1,
              })
            }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              type="primary"
              onClick={() => {
                confirm?.()
                updateSearchParams({
                  name: selectedKeys[0] as string,
                  pageNum: 1,
                })
              }}
              size="small"
              style={{ width: 90 }}
            >
              搜索
            </Button>
            <Button
              onClick={() => {
                clearFilters?.()
                updateSearchParams({ name: undefined, pageNum: 1 })
              }}
              size="small"
              style={{ width: 90 }}
            >
              重置
            </Button>
          </div>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
    },
    {
      title: '库存数量',
      dataIndex: 'inventory',
      key: 'inventory',
      width: 120,
    },
    {
      title: '所属考试',
      dataIndex: 'batchName',
      key: 'batchName',
      filteredValue: search.batchId
        ? (() => {
            const batchName = batches?.find(
              (b) => b.id === search.batchId,
            )?.name
            return batchName ? [batchName] : undefined
          })()
        : undefined,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Select
            placeholder="选择考试"
            value={selectedKeys[0]}
            onChange={(value) => {
              setSelectedKeys(value ? [value] : [])
            }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
            allowClear
            options={batches?.map((batch) => ({
              value: batch.name,
              label: batch.name,
            }))}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              type="primary"
              onClick={() => {
                confirm?.()
                updateSearchParams({
                  batchId: batches?.find((b) => b.name === selectedKeys[0])?.id,
                  pageNum: 1,
                })
              }}
              size="small"
              style={{ width: 90 }}
            >
              搜索
            </Button>
            <Button
              onClick={() => {
                clearFilters?.()
                updateSearchParams({ batchId: undefined, pageNum: 1 })
              }}
              size="small"
              style={{ width: 90 }}
            >
              重置
            </Button>
          </div>
        </div>
      ),
      render: (batchName: string) => <Link to="/batch">{batchName}</Link>,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          <Button size="small" onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Button
            size="small"
            danger
            onClick={() => handleDelete(record.id)}
            loading={deleteMutation.isPending}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ]

  // 处理新增
  const handleAdd = () => {
    setCurrentPosition(null)
    form.resetFields()
    setIsModalOpen(true)
  }

  // 处理编辑
  const handleEdit = (position: PositionData) => {
    setCurrentPosition(position)
    // 根据批次名称找到对应的批次ID
    const batch = batches?.find((b) => b.name === position.batchName)
    form.setFieldsValue({
      name: position.name,
      inventory: position.inventory,
      batchId: batch?.id,
    })
    setIsModalOpen(true)
  }

  // 处理提交（新增或修改）
  const handleSubmit = () => {
    form.validateFields().then((values) => {
      if (currentPosition) {
        // 更新操作，只更新库存
        updateMutation.mutate(
          {
            id: currentPosition.id,
            inventory: values.inventory,
          },
          {
            onSuccess: () => {
              setIsModalOpen(false)
              form.resetFields()
              setCurrentPosition(null)
            },
          },
        )
      } else {
        // 新增操作
        createMutation.mutate(
          {
            name: values.name,
            inventory: values.inventory,
            batchId: values.batchId,
          },
          {
            onSuccess: () => {
              setIsModalOpen(false)
              form.resetFields()
              setCurrentPosition(null)
            },
          },
        )
      }
    })
  }

  // 处理删除
  const handleDelete = (id: number) => {
    Modal.confirm({
      title: '确认删除',
      content: <div>
        <p className="text-sm text-red-500">岗位更新可能不及时，最长延迟10分钟，请在报名前完成职位代码上传</p>
        确定要删除该岗位吗？
      </div>,
      onOk: () => {
        deleteMutation.mutate(id)
      },
    })
  }

  // 处理批量导入
  const handleImport = () => {
    setIsImportModalOpen(true)
    importForm.resetFields()
    setFileList([])
  }

  // 处理导入提交
  const handleImportSubmit = () => {
    importForm.validateFields().then((values) => {
      if (fileList.length === 0) {
        message.error('请选择要导入的文件')
        return
      }

      const file = fileList[0].originFileObj || fileList[0]
      importMutation.mutate(
        {
          file,
          batchId: values.batchId,
        },
        {
          onSuccess: () => {
            setIsImportModalOpen(false)
            importForm.resetFields()
            setFileList([])
          },
        },
      )
    })
  }

  // 处理下载模板
  const handleDownloadTemplate = () => {
    downloadMutation.mutate()
  }

  // 处理分页变化
  const handlePaginationChange = (page: number, pageSize: number) => {
    updateSearchParams({
      pageNum: page,
      pageSize: pageSize,
    })
  }

  // 处理取消
  const handleCancel = () => {
    setIsModalOpen(false)
    form.resetFields()
    setCurrentPosition(null)
  }

  const handleImportCancel = () => {
    setIsImportModalOpen(false)
    importForm.resetFields()
    setFileList([])
  }

  // 处理批量删除
  const handleBatchDelete = () => {
    setIsBatchDeleteModalOpen(true)
    batchDeleteForm.resetFields()
  }

  // 处理批量删除提交
  const handleBatchDeleteSubmit = () => {
    batchDeleteForm.validateFields().then((values) => {
      batchDeleteMutation.mutate(values.batchId, {
        onSuccess: () => {
          setIsBatchDeleteModalOpen(false)
          batchDeleteForm.resetFields()
        },
      })
    })
  }

  // 处理批量删除取消
  const handleBatchDeleteCancel = () => {
    setIsBatchDeleteModalOpen(false)
    batchDeleteForm.resetFields()
  }

  // 加载状态
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    )
  }

  // 错误状态
  if (error) {
    return (
      <div className="p-4">
        <Alert
          message="加载失败"
          description={error.message}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={() => window.location.reload()}>
              重试
            </Button>
          }
        />
      </div>
    )
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">岗位管理</h2>
        <Space>
          <Button danger onClick={handleBatchDelete}>
            批量删除
          </Button>
          <Button onClick={handleImport}>批量导入</Button>
          <Button type="primary" onClick={handleAdd}>
            新增岗位
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={positionsData?.records || []}
        rowKey="id"
        bordered
        loading={isLoading}
        pagination={getPagination(
          positionsData?.current || 1,
          positionsData?.size || 10,
          positionsData?.total || 0,
          handlePaginationChange,
        )}
      />

      <Modal
        title={currentPosition ? '编辑岗位' : '新增岗位'}
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={handleCancel}
        width={600}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
      >
        <Form form={form} layout="vertical" className="mt-6">
        <p className="text-sm text-red-500">岗位更新可能不及时，最长延迟10分钟，请在报名前完成职位代码上传</p>
        <Form.Item
            label="岗位名称"
            name="name"
            rules={[{ required: true, message: '请输入岗位名称' }]}
          >
            <Input
              placeholder="请输入岗位名称"
              disabled={!!currentPosition} // 编辑时禁用名称修改
            />
          </Form.Item>

          <Form.Item
            label="库存数量"
            name="inventory"
            rules={[
              { required: true, message: '请输入库存数量' },
              { type: 'number', min: 0, message: '库存不能为负数' },
            ]}
          >
            <InputNumber className="w-full" min={0} />
          </Form.Item>

          {!currentPosition && (
            <Form.Item
              label="所属考试"
              name="batchId"
              rules={[{ required: true, message: '请选择所属考试' }]}
            >
              <Select
                placeholder="请选择所属考试"
                options={batches?.map((b) => ({
                  value: b.id,
                  label: b.name,
                }))}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title="批量导入岗位"
        open={isImportModalOpen}
        onOk={handleImportSubmit}
        onCancel={handleImportCancel}
        width={700}
        okText="导入"
        cancelText="取消"
        confirmLoading={importMutation.isPending}
      >
        <Form form={importForm} layout="vertical" className="mt-6">
        <p className="text-sm text-red-500">岗位更新可能不及时，最长延迟10分钟，请在报名前完成职位代码上传</p>
        <Form.Item
            label="所属考试"
            name="batchId"
            rules={[{ required: true, message: '请选择所属考试' }]}
          >
            <Select
              placeholder="请选择所属考试"
              options={batches?.map((b) => ({
                value: b.id,
                label: b.name,
              }))}
            />
          </Form.Item>

          <Form.Item label="下载模板">
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
              loading={downloadMutation.isPending}
            >
              下载模板
            </Button>
          </Form.Item>

          <Form.Item
            label="上传文件"
            name="file"
            rules={[{ required: true, message: '请上传文件' }]}
          >
            <Upload
              accept=".xlsx,.xls"
              beforeUpload={() => false}
              maxCount={1}
              fileList={fileList}
              onChange={({ fileList }) => setFileList(fileList)}
            >
              <Button icon={<UploadOutlined />}>点击上传</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="批量删除岗位"
        open={isBatchDeleteModalOpen}
        onOk={handleBatchDeleteSubmit}
        onCancel={handleBatchDeleteCancel}
        width={600}
        okText="确认删除"
        okButtonProps={{ danger: true }}
        cancelText="取消"
        confirmLoading={batchDeleteMutation.isPending}
      >
        <Alert
          message="警告：该功能仅限测试时使用！"
          description="删除被使用的批次会导致数据一致性问题，请谨慎操作。此操作将强制删除选中批次下的所有岗位数据，且无法恢复。"
          type="error"
          showIcon
          className="mb-6 mt-6"
        />
        <Form form={batchDeleteForm} layout="vertical">
          <Form.Item
            label="选择要删除的考试批次"
            name="batchId"
            rules={[{ required: true, message: '请选择要删除的考试批次' }]}
          >
            <Select
              placeholder="请选择考试批次"
              options={batches?.map((b) => ({
                value: b.id,
                label: b.name,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
